/**
 * Dosya indirme — web ve React Native WebView icin tek giris noktasi.
 *
 * NEDEN VAR
 * ---------
 * Projede 28 dosya su kalibi kopyalamisti:
 *   createObjectURL(new Blob([res.data])) -> <a download> -> click()
 * Bu kalip RN WebView'da CALISMIYOR: `blob:` URL'i ve `download` attribute'u
 * native tarafta karsiliksiz, kullanici butona basiyor ve hicbir sey olmuyor
 * (hata da verilmiyor). Ayrica kopyalarin cogunda revokeObjectURL yok.
 *
 * KULLANIM
 *   import { downloadFromUrl, exportFileName } from '@/utils/fileDownload'
 *
 *   await downloadFromUrl({
 *     url: '/api/user/export',
 *     params: filters,          // GET query string
 *     headers: this.apiHeaders,
 *     fileName: exportFileName('users_list'),
 *   })
 *
 * HANGISI?
 *   downloadFromUrl      GET  — web + MOBIL calisir. Export'larda bunu kullan.
 *   downloadFromRequest  POST — sadece WEB. Native `SAVE_FILE` bridge'i RN
 *                        tarafinda henuz implemente degil, mobilde
 *                        NATIVE_SAVE_TIMEOUT ile duser.
 *
 * NATIVE SOZLESMESI (React Native tarafinda implemente edilmeli)
 * -------------------------------------------------------------
 * Web -> Native   SAVE_FILE      { requestId, fileName, mimeType, base64 }
 * Native -> Web   SAVE_FILE_DONE { requestId, ok: true }
 *                 SAVE_FILE_DONE { requestId, ok: false, error: '...' }
 *
 * Native tarafta beklenen: base64'u dosyaya yaz (FileSystem.writeAsStringAsync,
 * encoding: base64), sonra paylas/ac (Sharing.shareAsync).
 * ACK dosya YAZILDIKTAN sonra gonderilmeli — paylasim sheet'i kapanmasi
 * beklenmemeli, yoksa web tarafi gereksiz yere bekler.
 *
 * SAVE_FILE_DONE gelmezse NATIVE_SAVE_TIMEOUT ile reddedilir; boylece
 * eksik native implementasyonu sessiz degil, gorunur bir hata uretir.
 */

import axios from "axios";
import moment from "moment";
import bridge from "@/core/services/bridge.service";
import native from "@/mobile/mobile.fileHandler";

export const XLSX_MIME =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Hem web hem native hata yolunda ayni son-care metni
const DOWNLOAD_FAILED_TEXT = "Dosya indirilemedi";

/**
 * Export dosya adi uretir:  users_list_2026-09-28_13-23-45.xlsx
 *
 * Neden bu format:
 * - Eski `YYYYMMDDHHMMSS` hem okunmuyordu hem de HATALIYDI: moment'te
 *   `MM` ay, dakika `mm`; `SS` saniye degil salise. Yani uretilen ad
 *   yil-ay-gun-saat-AY-salise idi, dakika ve saniye hic yoktu.
 * - `YYYY-MM-DD` tercih edildi cunku dosya yoneticisinde isme gore
 *   siralama kronolojik siralamayla ayni sonucu verir; `DD-MM-YYYY`
 *   bunu bozar.
 * - Saniye var: ayni dakika icinde alinan iki export'un adi cakismasin.
 * - Ayrac `:` DEGIL `-`: Windows ve macOS dosya adinda `:` kabul etmiyor.
 */
export function exportFileName(prefix, extension = "xlsx") {
    return `${prefix}_${moment().format("YYYY-MM-DD_HH-mm-ss")}.${extension}`;
}

const NATIVE_SAVE_TIMEOUT_MS = 30000;

let requestCounter = 0;

function nextRequestId() {
    requestCounter += 1;
    return `dl_${Date.now()}_${requestCounter}`;
}

/**
 * responseType: 'blob' oldugunda HATA govdesi de Blob gelir; JSON mesaji
 * okunamaz hale duser ve cagiran taraf "bir seyler oldu" demekle kalir.
 * Bu yardimci blob'u okuyup gercek mesaji cikarir.
 */
export async function readBlobError(error) {
    const data = error?.response?.data;

    if (data instanceof Blob) {
        try {
            const text = await data.text();
            const parsed = JSON.parse(text);
            return parsed?.message || parsed?.error || text;
        } catch (e) {
            // JSON degilse ham metni dondur
        }
    }

    return (
        error?.response?.data?.message ||
        error?.message ||
        DOWNLOAD_FAILED_TEXT
    );
}

function blobToBase64(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        // readAsDataURL "data:<mime>;base64,XXXX" uretir; yalniz XXXX gerekli
        reader.onload = () => resolve(String(reader.result).split(",")[1]);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
    });
}

function saveBlobNative(blob, fileName, mimeType) {
    return new Promise((resolve, reject) => {
        blobToBase64(blob)
            .then((base64) => {
                const requestId = nextRequestId();
                let timer = null;

                const unsubscribe = bridge.on("SAVE_FILE_DONE", (payload) => {
                    if (payload?.requestId !== requestId) return;

                    clearTimeout(timer);
                    unsubscribe();

                    if (payload.ok) {
                        resolve(true);
                    } else {
                        reject(new Error(payload.error || "NATIVE_SAVE_FAILED"));
                    }
                });

                timer = setTimeout(() => {
                    unsubscribe();
                    reject(new Error("NATIVE_SAVE_TIMEOUT"));
                }, NATIVE_SAVE_TIMEOUT_MS);

                bridge.send("SAVE_FILE", {
                    requestId,
                    fileName,
                    mimeType,
                    base64,
                });
            })
            .catch(reject);
    });
}

function saveBlobWeb(blob, fileName) {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();

    // Bellek temizligi — kopyalanan kalipta bu adim yoktu
    setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(objectUrl);
    }, 100);

    return Promise.resolve(true);
}

/**
 * Elde edilmis bir Blob'u kaydeder. Web/native ayrimi burada yapilir.
 */
export function saveBlob(blob, fileName, mimeType = XLSX_MIME) {
    return bridge.isNative
        ? saveBlobNative(blob, fileName, mimeType)
        : saveBlobWeb(blob, fileName);
}

/**
 * Istegi web tarafinda atar (axios interceptor zinciri, token yenileme ve
 * api-url-policy aynen calisir), donen blob'u kaydeder.
 *
 * POST govdeli export'lar icin bu kullanilir; native bridge URL tabanli
 * oldugu icin govdeyi tasiyamiyor.
 */
export async function downloadFromRequest({
    url,
    method = "post",
    data = null,
    params = null,
    headers = {},
    fileName,
    mimeType = XLSX_MIME,
}) {
    const response = await axios({
        url,
        method,
        data,
        params,
        headers,
        responseType: "blob",
    });

    await saveBlob(response.data, fileName, mimeType);

    return true;
}

const NATIVE_DOWNLOAD_TIMEOUT_MS = 60000;

/**
 * Native indirmeyi baslatir ve `DOWNLOAD_DONE` ack'ini BEKLER.
 *
 * Beklemek sart: aksi halde cagiran taraftaki `finally { isLoading = false }`
 * daha istek ucarken calisir (spinner aninda kapanir) ve native'deki hata
 * hicbir yere dusmez — kullanici butona basar, hicbir sey olmaz.
 *
 * Ack sozlesmesi MyCertificates/index.vue'daki calisan kullanimla ayni.
 * Payload'in hata alani standart degil, bu yuzden savunmaci okunuyor.
 *
 * NOT: `DOWNLOAD_DONE` requestId tasimiyor, yani ack'ler eslestirilemiyor.
 * Cagiran taraflar butonu `isLoading` ile kilitledigi icin ayni anda tek
 * indirme oluyor; ayni sayfada paralel indirme eklenirse protokole
 * requestId girmesi gerekir.
 */
function downloadNative(url, fileName, mimeType, headers) {
    return new Promise((resolve, reject) => {
        let timer = null;
        let unsubscribe = null;

        const cleanup = () => {
            clearTimeout(timer);
            if (unsubscribe) unsubscribe();
        };

        unsubscribe = bridge.on("DOWNLOAD_DONE", (payload) => {
            cleanup();

            const failed =
                payload &&
                (payload.ok === false || payload.success === false || payload.error);

            if (failed) {
                // Native mesaji varsa aynen gecilir (teshis icin degerli),
                // yoksa kullaniciya kod degil okunur metin gosterilir.
                reject(new Error(payload.error || payload.message || DOWNLOAD_FAILED_TEXT));
            } else {
                resolve(true);
            }
        });

        // Ack hic gelmezse sonsuza kadar bekleme
        timer = setTimeout(() => {
            cleanup();
            reject(new Error(`${DOWNLOAD_FAILED_TEXT} (zaman aşımı)`));
        }, NATIVE_DOWNLOAD_TIMEOUT_MS);

        // downloadFile senkron atabilir (URL politikasi / SecureLS);
        // o durumda dinleyiciyi ve zamanlayiciyi hemen birak.
        try {
            native.downloadFile(url, fileName, mimeType, headers);
        } catch (error) {
            cleanup();
            reject(error);
        }
    });
}

/**
 * GET adresinden indirir. Export'lar icin TERCIH EDILEN yol.
 *
 * Native tarafta MEVCUT `DOWNLOAD_FILE` bridge'i kullanilir — yeni protokol
 * gerekmez. Calisan referans: MyCertificates/index.vue (sertifika PDF'i).
 * Native istegi kendisi atar; Bearer'i api-url-policy ekler, relative URL
 * de cozulur.
 *
 * Web'de link dogrudan acilamaz (Bearer gerekiyor), bu yuzden axios ile
 * cekilip saveBlob'a veriliyor.
 *
 * FILTRELER: `params` olarak verilir. Bridge URL tabanli oldugu icin native
 * yolda query string'e yazilmalari gerekir; bunu axios'un kendi
 * serilestiricisi (`axios.getUri`) yapar, boylece web ile native birebir
 * ayni URL'i uretir (dizi -> `ids[]=1&ids[]=2`, nesne -> `f[a]=1`).
 */
export function downloadFromUrl({
    url,
    fileName,
    mimeType = XLSX_MIME,
    params = null,
    headers = {},
}) {
    if (bridge.isNative) {
        const nativeUrl = params ? axios.getUri({ url, params }) : url;

        return downloadNative(nativeUrl, fileName, mimeType, headers);
    }

    return downloadFromRequest({
        url,
        method: "get",
        params,
        headers,
        fileName,
        mimeType,
    });
}

export default {
    saveBlob,
    downloadFromRequest,
    downloadFromUrl,
    readBlobError,
    exportFileName,
    XLSX_MIME,
};
